"use client";

/**
 * /settings/integrations — connect + manage Drive / Dropbox / Calendar OAuth.
 *
 * Retires the D1/D2 dead "Coming soon" buttons on the artifacts page by giving
 * users a real place to connect providers. Per Phase 1-3a of the 2026-04-23
 * integrations spec — backend is live (3 providers, read + write), this is
 * the user-facing entry point.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Cloud, FolderOpen, Calendar, Check, AlertTriangle, RefreshCcw, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { DrivePickerModal } from "@/components/integrations/drive-picker-modal";
import { ConnectedAppsSection } from "@/components/integrations/connected-apps-section";
import type { Agent } from "@/lib/types";

type ProviderId = "google_drive" | "dropbox" | "google_calendar";

interface ConnectedRow {
  id: string;
  provider: ProviderId;
  account_label: string;
  status: "connected" | "disconnected" | "needs_reauth" | "error";
  scopes: string[];
  external_account_id: string;
  connected_at: string;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

interface AvailableRow {
  provider: string;
  name: string;
  supported_tiers: number[];
}

const PROVIDER_META: Record<ProviderId, { name: string; icon: typeof Cloud; blurb: string }> = {
  google_drive: {
    name: "Google Drive",
    icon: FolderOpen,
    blurb: "Attach folders to an agent. Drive files can be listed, searched, downloaded, and uploaded.",
  },
  dropbox: {
    name: "Dropbox",
    icon: Cloud,
    blurb: "Attach paths to an agent. Full Dropbox scope — list, search, download, upload.",
  },
  google_calendar: {
    name: "Google Calendar",
    icon: Calendar,
    blurb: "Attach calendars so the agent can read and create events on your behalf.",
  },
};

function statusLabel(s: ConnectedRow["status"]): string {
  switch (s) {
    case "connected":      return "Connected";
    case "needs_reauth":   return "Reconnect needed";
    case "disconnected":   return "Disconnected";
    case "error":          return "Error";
  }
}

function statusClasses(s: ConnectedRow["status"]): string {
  switch (s) {
    case "connected":      return "bg-[var(--success-container)] text-[var(--on-success-container)]";
    case "needs_reauth":   return "bg-[var(--warning-container)] text-[var(--on-warning-container)]";
    case "disconnected":   return "bg-[var(--surface-high)] text-[var(--on-surface-variant)]";
    case "error":          return "bg-[var(--error-container)] text-[var(--on-error-container)]";
  }
}

export default function IntegrationsPage() {
  const search = useSearchParams();
  const [connected, setConnected] = useState<ConnectedRow[]>([]);
  const [available, setAvailable] = useState<AvailableRow[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "success" | "error"; msg: string } | null>(null);
  const [pickerFor, setPickerFor] = useState<{ integrationId: string; agentId: string; agentName: string } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((kind: "success" | "error", msg: string) => {
    setToast({ kind, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listResp, agentList] = await Promise.all([
        api.integrations.list(),
        api.agents.list().catch(() => [] as Agent[]),
      ]);
      setConnected(listResp.connected as ConnectedRow[]);
      setAvailable(listResp.available);
      setAgents(agentList);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load integrations";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Handle ?connected=X and ?error=X query params from the OAuth callback.
  const handledCallbackRef = useRef(false);
  useEffect(() => {
    if (handledCallbackRef.current) return;
    const connectedParam = search.get("connected");
    const errorParam = search.get("error");
    if (connectedParam) {
      showToast("success", `${PROVIDER_META[connectedParam as ProviderId]?.name ?? connectedParam} connected.`);
      handledCallbackRef.current = true;
    } else if (errorParam) {
      showToast("error", `OAuth error: ${errorParam}`);
      handledCallbackRef.current = true;
    }
  }, [search, showToast]);

  const handleConnect = useCallback(
    async (provider: ProviderId) => {
      try {
        const resp = await api.integrations.authorize(provider);
        if (resp.authorize_url) {
          window.location.href = resp.authorize_url;
        } else {
          showToast("error", "No authorize URL returned by server");
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to start OAuth";
        showToast("error", msg);
      }
    },
    [showToast],
  );

  const handleDisconnect = useCallback(
    async (row: ConnectedRow) => {
      const ok = window.confirm(
        `Disconnect ${PROVIDER_META[row.provider].name} (${row.account_label})? Already-attached resources stay; future sync stops.`,
      );
      if (!ok) return;
      try {
        await api.integrations.disconnect(row.id);
        showToast("success", `${PROVIDER_META[row.provider].name} disconnected.`);
        await refresh();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Disconnect failed";
        showToast("error", msg);
      }
    },
    [refresh, showToast],
  );

  const availableProviders = useMemo(() => {
    const connectedSet = new Set(connected.map(c => c.provider));
    return available.filter(a => !connectedSet.has(a.provider as ProviderId));
  }, [available, connected]);

  const defaultAgent = agents.find(a => a.source === "platform") ?? agents[0];

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <header>
        <h1 className="text-xl font-bold text-[var(--on-surface)]">Integrations</h1>
        <p className="mt-1 text-sm text-[var(--on-surface-variant)]">
          Connect third-party services so your agents can work with your real data.
        </p>
      </header>

      {toast && (
        <div
          className={[
            "px-4 py-2.5 rounded-lg text-sm flex items-center gap-2",
            toast.kind === "success"
              ? "bg-[var(--success-container)] text-[var(--on-success-container)]"
              : "bg-[var(--error-container)] text-[var(--on-error-container)]",
          ].join(" ")}
        >
          {toast.kind === "success" ? <Check className="size-4" /> : <AlertTriangle className="size-4" />}
          {toast.msg}
        </div>
      )}

      {error && (
        <div className="px-4 py-2.5 rounded-lg text-sm bg-[var(--error-container)] text-[var(--on-error-container)]">
          {error}
        </div>
      )}

      <ConnectedAppsSection onToast={showToast} />

      {/* The native Drive/Dropbox/Calendar path. A separate system from the
          section above: our own OAuth, our own vault tokens, scoped per ACCOUNT
          rather than per user. Labelled so the difference is visible. */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-[var(--on-surface)]">File storage &amp; calendar</h2>
        <p className="text-xs text-[var(--on-surface-variant)]">
          Shared with your whole workspace. Attach folders and calendars to a specific agent.
        </p>
        {/* Google Drive legitimately appears in BOTH sections and they do
            different jobs. Saying so beats two identically-named buttons that
            behave differently — which is what a real run of this page hit. */}
        <p className="text-xs text-[var(--on-surface-variant)]">
          Google Drive appears above as well: that card lets your agents act in Drive,
          this one attaches specific folders. Connecting here redirects this page to the
          provider and brings you back, rather than opening a new tab.
        </p>
        {loading && (
          <p className="text-xs text-[var(--on-surface-variant)] italic">Loading…</p>
        )}
        {!loading && connected.length === 0 && (
          <p className="text-xs text-[var(--on-surface-variant)] italic">No integrations connected yet.</p>
        )}
        {connected.map(row => {
          const meta = PROVIDER_META[row.provider];
          const Icon = meta?.icon ?? Cloud;
          return (
            <div
              key={row.id}
              className="rounded-xl border border-[var(--outline-variant)] bg-[var(--surface-container)] p-4 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Icon className="size-5 text-[var(--primary)] shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--on-surface)]">{meta?.name ?? row.provider}</p>
                    <p className="text-xs text-[var(--on-surface-variant)] truncate">{row.account_label}</p>
                  </div>
                </div>
                <span className={["px-2 py-0.5 rounded-md text-[10px] font-semibold shrink-0", statusClasses(row.status)].join(" ")}>
                  {statusLabel(row.status)}
                </span>
              </div>

              {row.last_sync_error && (
                <p className="text-xs text-[var(--error)] bg-[var(--error-container)]/40 px-2 py-1 rounded">
                  {row.last_sync_error}
                </p>
              )}

              <div className="flex items-center gap-2 pt-2">
                {row.provider === "google_drive" && row.status === "connected" && defaultAgent && (
                  <button
                    type="button"
                    onClick={() =>
                      setPickerFor({
                        integrationId: row.id,
                        agentId: defaultAgent.id,
                        agentName: defaultAgent.custom_name ?? defaultAgent.name,
                      })
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90 transition-opacity"
                  >
                    <Plus className="size-3.5" /> Attach folder
                  </button>
                )}
                {row.status === "needs_reauth" && (
                  <button
                    type="button"
                    onClick={() => handleConnect(row.provider)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--warning-container)] text-[var(--on-warning-container)] hover:opacity-90"
                  >
                    <RefreshCcw className="size-3.5" /> Reconnect
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDisconnect(row)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)] transition-colors ml-auto"
                >
                  <Trash2 className="size-3.5" /> Disconnect
                </button>
              </div>
            </div>
          );
        })}
      </section>

      {/* Available */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-[var(--on-surface)]">Available</h2>
        {!loading && availableProviders.length === 0 && connected.length > 0 && (
          <p className="text-xs text-[var(--on-surface-variant)] italic">You've connected all supported providers.</p>
        )}
        {availableProviders.map(a => {
          const pid = a.provider as ProviderId;
          const meta = PROVIDER_META[pid];
          const Icon = meta?.icon ?? Cloud;
          return (
            <div
              key={a.provider}
              className="rounded-xl border border-[var(--outline-variant)] bg-[var(--surface-container)] p-4 flex items-center justify-between gap-4"
            >
              <div className="flex items-start gap-3 min-w-0">
                <Icon className="size-5 text-[var(--on-surface-variant)] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--on-surface)]">{meta?.name ?? a.name}</p>
                  <p className="text-xs text-[var(--on-surface-variant)]">{meta?.blurb}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleConnect(pid)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90 shrink-0"
              >
                Connect
              </button>
            </div>
          );
        })}
      </section>

      {pickerFor && (
        <DrivePickerModal
          integrationId={pickerFor.integrationId}
          agentId={pickerFor.agentId}
          agentName={pickerFor.agentName}
          onClose={() => setPickerFor(null)}
          onAttached={(count) => {
            showToast("success", `Attached ${count} folder${count === 1 ? "" : "s"} to ${pickerFor.agentName}.`);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
